/**
 * @file EmployeeLeaveManagement.jsx
 * @description Advanced Salesforce Lightning executive console with real-time analytics indicators.
 * @module modules/leave-management/pages/EmployeeLeaveManagement
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LeaveTable from './LeaveTable';
import API from '../api/client';
import { leaveService } from '../services/leaveService';
import {
    Box, Container, Typography, Breadcrumbs, Paper, Grid,
    TextField, MenuItem, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import { Add, Refresh, Assessment, Equalizer, BarChart } from '@mui/icons-material';
import { toast } from 'react-toastify';

// --- SUBSYSTEM: SALESFORCE LIGHTNING ANALYTICS HISTOGRAM MATRIX ---
const NativeAnalyticsChart = ({ approved, pending, rejected, total }) => {
    const totalCalc = total || 1;
    const pctApp = Math.round((approved / totalCalc) * 100) || 0;
    const pctPen = Math.round((pending / totalCalc) * 100) || 0;
    const pctRej = Math.round((rejected / totalCalc) * 100) || 0;

    return (
        <Box sx={{ p: 2.5, bgcolor: '#ffffff', border: '1px solid #dddbda', borderRadius: '4px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#514f4d', mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Equalizer fontSize="small" /> LIVE DISTRIBUTION VOLUME ANALYTICS QUANTIZATION MATRIX
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#2e844a' }}>APPROVED STATUS WEIGHT</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>{pctApp}% ({approved})</Typography>
                    </Box>
                    <Box sx={{ width: '100%', bgcolor: '#e0e0e0', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                        <Box sx={{ width: `${pctApp}%`, bgcolor: '#2e844a', height: '100%', transition: 'width 0.5s' }} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#dd7a01' }}>PENDING REVIEWS PIPELINE</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>{pctPen}% ({pending})</Typography>
                    </Box>
                    <Box sx={{ width: '100%', bgcolor: '#e0e0e0', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                        <Box sx={{ width: `${pctPen}%`, bgcolor: '#dd7a01', height: '100%', transition: 'width 0.5s' }} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#ea001e' }}>REJECTED EXCEPTION WEIGHT</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>{pctRej}% ({rejected})</Typography>
                    </Box>
                    <Box sx={{ width: '100%', bgcolor: '#e0e0e0', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                        <Box sx={{ width: `${pctRej}%`, bgcolor: '#ea001e', height: '100%', transition: 'width 0.5s' }} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

// --- CORE CONTROLLER COMPONENT MASTER CONSOLE ---
const EmployeeLeaveManagement = ({ role = 'BDE' }) => {
    const [leaves, setLeaves] = useState([]);
    const [attendanceLogs, setAttendanceLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedYear, setSelectedYear] = useState('2026');
    const [formData, setFormData] = useState({ leave_type: 'Casual', start_date: '', end_date: '', reason: '' });

    const fetchLeaveWorkspaceData = useCallback(async () => {
        setLoading(true);
        try {
            const leavePromise = (role === 'BDE') ? leaveService.getMyLeaves() : leaveService.getSummary();
            const attendancePromise = API.get(`/attendance/history`);
            const [leaveRes, attendanceRes] = await Promise.allSettled([leavePromise, attendancePromise]);

            if (leaveRes.status === 'fulfilled') {
                const dataPayload = leaveRes.value?.data?.data || leaveRes.value?.data || [];
                const filtered = (Array.isArray(dataPayload) ? dataPayload : []).filter(item => {
                    const d = new Date(item.start_date);
                    return (selectedYear === 'All Years' || d.getFullYear().toString() === selectedYear) &&
                        (selectedMonth === 'all' || (d.getMonth() + 1).toString().padStart(2, '0') === selectedMonth);
                });
                setLeaves(filtered);
            }

            if (attendanceRes.status === 'fulfilled') {
                const logs = attendanceRes.value?.data?.logs || [];
                const filteredLogs = logs.filter(item => {
                    const d = new Date(item.date);
                    return (selectedYear === 'All Years' || d.getFullYear().toString() === selectedYear) &&
                        (selectedMonth === 'all' || (d.getMonth() + 1).toString().padStart(2, '0') === selectedMonth);
                });
                setAttendanceLogs(filteredLogs);
            }
        } catch (err) {
            console.error("Critical console parsing engine loop crash.", err);
        } finally {
            setLoading(false);
        }
    }, [role, selectedMonth, selectedYear]);

    useEffect(() => {
        fetchLeaveWorkspaceData();
    }, [fetchLeaveWorkspaceData]);

    const metrics = useMemo(() => {
        const approved = leaves.filter(l => String(l.manager_status || l.status).toLowerCase().trim() === 'approved').length;
        const pending = leaves.filter(l => String(l.manager_status || l.status).toLowerCase().trim() === 'pending').length;
        const rejected = leaves.filter(l => String(l.manager_status || l.status).toLowerCase().trim() === 'rejected').length;
        return { approved, pending, rejected, total: leaves.length };
    }, [leaves]);

    const handleSubmitNewRecord = async () => {
        if (!formData.start_date || !formData.end_date || !formData.reason.trim()) {
            return toast.error("Complete standard transactional parameter blocks.");
        }
        try {
            await leaveService.submitRequest(formData);
            toast.success("Successfully generated new record payload index row entry.");
            setDialogOpen(false);
            setFormData({ leave_type: 'Casual', start_date: '', end_date: '', reason: '' });
            fetchLeaveWorkspaceData();
        } catch (err) {
            toast.error("Pipeline framework write access drop rejection anomaly.");
        }
    };

    return (
        <Box sx={{ bgcolor: '#f3f2f2', minHeight: '100vh' }}>
            {/* STICKY HEADER & METRICS CONSOLE SECTION */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                {/* Salesforce Command Global Header Banner Grid */}
                <Paper sx={{ bgcolor: '#032d60', borderBottom: '3px solid #0176d3', p: 2, px: 3, boxShadow: 'none' }} square variant="outlined">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ bgcolor: '#0176d3', color: '#ffffff', p: 1, borderRadius: '4px', display: 'flex' }}>
                                <BarChart />
                            </Box>
                            <Box>
                                <Breadcrumbs separator="›" sx={{ fontSize: '11px', '& .MuiBreadcrumbs-separator': { color: '#b0adab' } }}>
                                    <Typography sx={{ fontWeight: 600, color: '#b0adab' }}>Attendance & Leave Management</Typography>
                                </Breadcrumbs>
                                <Typography variant="h1" sx={{ fontWeight: 800, color: '#ffffff', mt: 0.2, fontSize: '16px' }}>
                                    Human Resource Telemetry Console Workspace
                                </Typography>
                            </Box>
                        </Box>

                        <Stack direction="row" spacing={1.5} alignItems="center">
                            {/* TARGET MONTH DROPDOWN (Resolves setSelectedMonth error) */}
                            <TextField select size="small" label="Target Month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} sx={{ bgcolor: '#fff', minWidth: '140px', '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}>
                                <MenuItem value="all">All Months</MenuItem>
                                <MenuItem value="01">January</MenuItem>
                                <MenuItem value="02">February</MenuItem>
                                <MenuItem value="03">March</MenuItem>
                                <MenuItem value="04">April</MenuItem>
                                <MenuItem value="05">May</MenuItem>
                                <MenuItem value="06">June</MenuItem>
                                <MenuItem value="07">July</MenuItem>
                                <MenuItem value="08">August</MenuItem>
                                <MenuItem value="09">September</MenuItem>
                                <MenuItem value="10">October</MenuItem>
                                <MenuItem value="11">November</MenuItem>
                                <MenuItem value="12">December</MenuItem>
                            </TextField>

                            <TextField select size="small" label="Target Year" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} sx={{ bgcolor: '#fff', minWidth: '130px', '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}>
                                <MenuItem value="All Years">All Target Years</MenuItem>
                                <MenuItem value="2025">2025 Execution</MenuItem>
                                <MenuItem value="2026">2026 Execution</MenuItem>
                                <MenuItem value="2027">2027 Execution</MenuItem>
                            </TextField>

                            <Button variant="outlined" onClick={fetchLeaveWorkspaceData} startIcon={<Refresh />} sx={{ textTransform: 'none', fontWeight: 700, color: '#0176d3', borderColor: '#dddbda', height: '36px', borderRadius: '4px', bgcolor: '#fff', '&:hover': { bgcolor: '#f4f6f9', borderColor: '#b0adab' } }}>
                                Refresh
                            </Button>

                            {role === 'BDE' && (
                                <Button variant="contained" onClick={() => setDialogOpen(true)} startIcon={<Add />} sx={{ bgcolor: '#0176d3', textTransform: 'none', fontWeight: 700, height: '36px', borderRadius: '4px', boxShadow: 'none', '&:hover': { bgcolor: '#014486' } }}>
                                    Request Leave
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Paper>

                {/* STICKY ANALYTICS BLOCK CONTAINER */}
                <Box sx={{ bgcolor: '#f4f6f9', borderBottom: '1px solid #dddbda', px: 3, py: 1.5 }}>
                    <NativeAnalyticsChart approved={metrics.approved} pending={metrics.pending} rejected={metrics.rejected} total={metrics.total} />
                </Box>
            </Box>

            <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
                {/* Primary Related Records Master-Detail Subsystem Frame Box Container */}
                <Paper variant="outlined" sx={{ bgcolor: '#ffffff', borderColor: '#dddbda', borderRadius: '4px', overflow: 'hidden', boxShadow: 'none' }}>
                    <Box sx={{ p: 2, bgcolor: '#f3f2f2', borderBottom: '1px solid #dddbda', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assessment sx={{ color: '#0176d3', fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#181816' }}>
                            Active Console Interface: Object Record Datagrid Frame
                        </Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        {/* CONDITIONAL LOADER COMPONENT (Resolves loading error) */}
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, gap: 1.5 }}>
                                <CircularProgress size={28} sx={{ color: '#0176d3' }} />
                                <Typography sx={{ fontSize: '13px', color: '#514f4d', fontWeight: 500 }}>Synchronizing system parameters...</Typography>
                            </Box>
                        ) : (
                            <LeaveTable
                                role={role}
                                initialLeaves={leaves}
                                initialAttendance={attendanceLogs}
                                onDataMutation={fetchLeaveWorkspaceData}
                            />
                        )}
                    </Box>
                </Paper>
            </Container>

            {/* Salesforce Form Submission Dialog Overlay Modal */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ sx: { borderRadius: '4px', maxWidth: '520px', width: '100%' } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '16px', color: '#fff', bgcolor: '#0176d3', p: 2 }}>
                    Initialize New Absence Object Record Space
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField select size="small" label="Resource Type Pool Allocation" value={formData.leave_type} onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })} fullWidth>
                        <MenuItem value="Casual">Casual Discretionary Pool</MenuItem>
                        <MenuItem value="Medical">Medical Sick Quarantine Exception</MenuItem>
                        <MenuItem value="Earned">Paid Cumulative Annual Allocation</MenuItem>
                    </TextField>
                    <Stack direction="row" spacing={2}>
                        <TextField size="small" label="Overture Timeline Start" type="date" InputLabelProps={{ shrink: true }} value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} fullWidth />
                        <TextField size="small" label="Overture Timeline Termination" type="date" InputLabelProps={{ shrink: true }} value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} fullWidth />
                    </Stack>
                    <TextField label="Business Justification Analytical Rationale Statement" multiline rows={4} value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="State explicit justification profiles here..." fullWidth />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f3f2f2', borderTop: '1px solid #dddbda' }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#514f4d', fontWeight: 700 }}>Abort Entry</Button>
                    <Button onClick={handleSubmitNewRecord} variant="contained" sx={{ textTransform: 'none', bgcolor: '#0176d3', fontWeight: 700, boxShadow: 'none' }}>Commit Object Entry</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmployeeLeaveManagement;